import React, { useEffect, useMemo, useState } from "react";
import { Clock, BarChart2, Layers, Landmark, Info } from "lucide-react";

const LS_KEY = "qtr-inst-filters";
const DEFAULT_TOGGLES = { killzone: true, volume: true, htf: true, cot: false };

export function loadToggles() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...DEFAULT_TOGGLES, ...JSON.parse(raw) };
  } catch { /* noop */ }
  return DEFAULT_TOGGLES;
}

export function computeConfluence(inst, toggles) {
  if (!inst) return null;
  const checks = {
    killzone: inst.killzone?.passed,
    volume: inst.volume?.available ? inst.volume.passed : null,
    htf: inst.htf?.passed,
    cot: inst.cot?.available ? inst.cot.passed : null,
  };
  let score = 0, max = 0;
  Object.entries(toggles).forEach(([k, on]) => {
    if (!on) return;
    if (checks[k] === null || checks[k] === undefined) return;
    max += 1;
    if (checks[k]) score += 1;
  });
  return { score, max };
}

export default function InstitutionalFilters({ institutional, onScoreChange }) {
  const [toggles, setToggles] = useState(loadToggles);

  const confluence = useMemo(() => computeConfluence(institutional, toggles), [institutional, toggles]);

  useEffect(() => {
    if (onScoreChange) onScoreChange(confluence);
  }, [confluence, onScoreChange]);

  const setToggle = (key, value) => {
    setToggles((prev) => {
      const next = { ...prev, [key]: value };
      try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  if (!institutional) return null;
  const { killzone, volume, htf, cot } = institutional;

  return (
    <div className="panel p-5" data-testid="institutional-filters">
      <div className="flex items-center justify-between mb-3">
        <div className="panel-title text-[14px]">Filtres Institutionnels</div>
        {confluence && confluence.max > 0 && (
          <span
            className={`chip ${confluence.score === confluence.max ? "chip-up" : confluence.score === 0 ? "chip-down" : "chip-neutral"}`}
            title="Score = filtres validés / filtres activés ET évaluables (les filtres sans données, ex. volume forex, ne comptent pas)"
            data-testid="confluence-score"
          >
            Confluence {confluence.score}/{confluence.max}
          </span>
        )}
      </div>
      <div className="space-y-2">
        <FilterRow
          icon={Clock}
          name="Kill Zone (Session)"
          enabled={toggles.killzone}
          onToggle={(v) => setToggle("killzone", v)}
          passed={killzone?.passed}
          detail={killzone?.is_killzone
            ? `${killzone.session} · ${killzone.candle_time_utc}`
            : `${killzone?.session} · prochaine : ${killzone?.next_killzone || "—"}`}
          testId="filter-killzone"
        />
        <FilterRow
          icon={BarChart2}
          name="Volume (BOS ≥ 1.5× SMA20)"
          enabled={toggles.volume}
          onToggle={(v) => setToggle("volume", v)}
          passed={volume?.available ? volume.passed : null}
          detail={volume?.available
            ? `Ratio ${volume.ratio}× (vol. ${volume.current} vs moy. ${volume.avg_20})`
            : volume?.reason || "Indisponible"}
          testId="filter-volume"
        />
        <FilterRow
          icon={Layers}
          name="Top-Down (H1/H4/D1)"
          enabled={toggles.htf}
          onToggle={(v) => setToggle("htf", v)}
          passed={htf?.passed}
          detail={`Biais dominant : ${htf?.dominant_bias || "—"}`}
          testId="filter-htf"
        />
        <FilterRow
          icon={Landmark}
          name="COT (Commercials CFTC)"
          enabled={toggles.cot}
          onToggle={(v) => setToggle("cot", v)}
          passed={cot?.available ? cot.passed : null}
          detail={cot?.available
            ? `${cot.bias === "bullish" ? "Acheteur" : cot.bias === "bearish" ? "Vendeur" : "Neutre"}${cot.strong ? " (fort)" : ""} · Δ net ${cot.commercials_net_change > 0 ? "+" : ""}${cot.commercials_net_change} · rapport ${cot.report_date}`
            : cot?.reason || "Indisponible"}
          testId="filter-cot"
        />
      </div>
      {toggles.cot && cot?.available && (
        <div className="mt-3 flex items-start gap-2 text-[10px] text-warn/90 bg-warn/5 border border-warn/20 rounded-md px-2.5 py-2" data-testid="cot-delay-warning">
          <Info size={12} className="shrink-0 mt-0.5" />
          <span>{cot.delay_warning}</span>
        </div>
      )}
      <div className="mt-3 text-[10px] text-white/40 leading-relaxed border-t border-white/5 pt-2.5">
        Filtres optionnels : ils n&apos;empêchent pas l&apos;affichage du signal mais mesurent sa confluence
        avec l&apos;activité institutionnelle. Un score partiel n&apos;invalide pas un setup — il informe votre décision.
      </div>
    </div>
  );
}

function FilterRow({ icon: Icon, name, enabled, onToggle, passed, detail, testId }) {
  const status = !enabled ? "off" : passed === true ? "pass" : passed === false ? "fail" : "na";
  const statusStyle = {
    off: "text-white/30",
    pass: "text-up",
    fail: "text-down",
    na: "text-white/40",
  }[status];
  const statusLabel = { off: "Désactivé", pass: "✓ Validé", fail: "✗ Non aligné", na: "N/A" }[status];
  return (
    <div className={`flex items-center gap-3 rounded-md border border-white/5 bg-hover px-3 py-2 ${!enabled ? "opacity-50" : ""}`} data-testid={testId}>
      <Icon size={14} className="text-accent-blue shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-white/80">{name}</div>
        <div className="text-[10px] text-white/40 truncate" title={detail}>{detail}</div>
      </div>
      <span className={`text-[10px] font-semibold whitespace-nowrap ${statusStyle}`} data-testid={`${testId}-status`}>{statusLabel}</span>
      <input
        type="checkbox"
        className="accent-blue-500 cursor-pointer"
        checked={enabled}
        onChange={(e) => onToggle(e.target.checked)}
        data-testid={`${testId}-toggle`}
      />
    </div>
  );
}
